import { Tabs as Base } from '@base-ui/react/tabs'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'
import s from './Tabs.module.css'

/**
 * Tabs — onglets du design system, sur la primitive `@base-ui/react` (rôles ARIA,
 * navigation clavier, activation). L'apparence — la barre soulignée du prototype —
 * vient à 100 % du module CSS ; l'état actif est lu sur le `data-active` que publie
 * la primitive, jamais transformé en classe ici.
 *
 * Présentationnel pur et agnostique du métier : les onglets arrivent en props, les
 * panneaux en enfants (`TabPanel`), appariés par `value`.
 *
 * ⚠️ Agnostique de la densité : ses dimensions viennent de `--control-*`. Ne jamais en
 * dériver une variante « tactile ».
 */

/**
 * Deux dispositions, et la seconde n'est pas une variante d'apparence : elle dit **qui
 * défile**.
 *
 * Par défaut, les onglets se comportent comme n'importe quel bloc de contenu — ils sont
 * posés dans un `PageBody` et défilent avec lui.
 *
 * `fill` reproduit l'éditeur du prototype (`.ctx` + `[data-ed-panel]`) : les onglets
 * organisent la page entière, la barre reste sous la barre de titre et sur la même surface,
 * et c'est le panneau qui prend la hauteur restante. Le contenu de chaque onglet y pose
 * alors son propre `PageBody` — dans le prototype, chaque onglet rend un `.body.scroll`.
 *
 * ⚠️ `fill` ne vaut que pour des onglets **enfants directs de `Page`** : c'est de sa colonne
 * qu'ils tirent la hauteur à remplir.
 */
const root = cva(s.root, {
  variants: {
    layout: {
      inline: null,
      fill: s.fill,
    },
  },
  defaultVariants: { layout: 'inline' },
})

export interface TabItem<T extends string> {
  value: T
  /** Libellé de l'onglet — du texte, ou texte + icône/badge. */
  label: ReactNode
  disabled?: boolean
}

export interface TabsProps<T extends string> extends VariantProps<typeof root> {
  tabs: readonly TabItem<T>[]
  /** Onglet actif au montage (mode non contrôlé). */
  defaultValue?: T
  value?: T
  onValueChange?: (value: T) => void
  /** Les `TabPanel` correspondants. */
  children?: ReactNode
  className?: string
}

export function Tabs<T extends string>({
  tabs,
  defaultValue,
  value,
  onValueChange,
  layout,
  children,
  className,
}: TabsProps<T>) {
  return (
    <Base.Root
      defaultValue={defaultValue}
      value={value}
      // La primitive passe aussi les détails de l'événement, et élargit la valeur à
      // `string | number | null` : on referme le type sur celui des onglets fournis.
      onValueChange={(next) => {
        if (next !== null) onValueChange?.(next as T)
      }}
      className={root({ layout, className })}
    >
      <Base.List className={s.list}>
        {tabs.map((tab) => (
          <Base.Tab key={tab.value} value={tab.value} disabled={tab.disabled} className={s.tab}>
            {tab.label}
          </Base.Tab>
        ))}
      </Base.List>
      {children}
    </Base.Root>
  )
}

export interface TabPanelProps<T extends string> {
  value: T
  children?: ReactNode
  className?: string
  /**
   * Garder le contenu monté quand l'onglet n'est pas celui qu'on regarde. `false` par défaut,
   * comme la primitive.
   *
   * ⚠️ **À ne poser que sur un panneau dont l'état de travail ne vit nulle part ailleurs.** Le
   * défaut démonte, donc tout état local repart à sa valeur initiale au retour : sans danger
   * pour un panneau dont la saisie descend dans Dexie, coûteux pour un panneau qui tient une
   * pile d'annulation, un zoom ou une sélection — c'est-à-dire un travail que l'utilisateur
   * croit en cours et qu'aucune base ne rattrapera.
   */
  keepMounted?: boolean
}

export function TabPanel<T extends string>({
  value,
  children,
  className,
  keepMounted,
}: TabPanelProps<T>) {
  return (
    <Base.Panel value={value} className={cn(s.panel, className)} keepMounted={keepMounted}>
      {children}
    </Base.Panel>
  )
}
